import { useEffect, useState } from 'react';

interface UseFetchResult<T> {
  data: T | null;
  error: string | null;
  isLoading: boolean;
}

// Custom hook to fetch data from a given URL
export const useFetch = <T = unknown>(
  url: string,
  refreshCount: number = 0
): UseFetchResult<T> => {
  // State variables to hold data, error, and loading status
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // Effect to fetch data from the provided URL
  useEffect(() => {
    // AbortController to cancel fetch requests if the component unmounts
    const controller = new AbortController();
    const signal = controller.signal;

    // Options for the fetch request, including method, headers, and signal for aborting
    const options: RequestInit = {
      signal: signal,
      headers: {
        'Content-Type': 'application/json',
      },
    };

    // Function to fetch data from the URL
    const fetchData = async () => {
      try {
        // Fetch data from the URL with the specified options
        const response = await fetch(url, options);
        // Check if the response is ok (status in the range 200-299)
        if (!response.ok) {
          console.log(response);
          setError('Failed to fetch data from the backend API');
          throw new Error(
            'Network response was not ok, please contact support if the issue persists.'
          );
        }
        // Parse the response data as JSON
        const responseData = (await response.json()) as T;
        // Update state with the fetched data and reset error
        setData(responseData);
        setError(null);
      } catch (err) {
        if (err instanceof Error && err.name !== 'AbortError') {
          setError(err.message);
          console.error('Fetch error:', err);
        }
      } finally {
        // Set loading state to false after fetch completes
        setIsLoading(false);
      }
    };

    // If a URL is provided, start fetching data
    if (url) {
      //  Set loading state to true before starting the fetch
      setIsLoading(true);
      fetchData();
      // Cleanup function to abort the fetch request if the component unmounts
      return () => {
        controller.abort();
      };
    }
  }, [url, refreshCount]);

  // Return the fetched data, any error encountered, and the loading state
  return { data, error, isLoading };
};

